import React from "react";
import { Document, Page, View, Text, Image, Font } from "@react-pdf/renderer";
import styles from "../../components/Assets/generateBill/billStyles";
import companyLogo from "../../assets/images/login_logo.png";
import { CompanyInfo } from "../../models/quotationData.model";
import { CustomerOrder, OrderItem } from "../../models/Order.model";

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfBBc4AMP6lbBP.ttf",
      fontWeight: "bold",
    },
  ],
});

interface OrderConfirmationPDFProps {
  orderItems: OrderItem[];
  customerInfo: CompanyInfo;
  customerOrders: CustomerOrder[];
  grandTotal: number;
  transportPrice?: number;
  discountPrice?: number;
}

export const OrderConfirmationPDF: React.FC<OrderConfirmationPDFProps> = ({
  orderItems,
  customerInfo,
  customerOrders,
  transportPrice = 0,
}) => {
  const {
    name,
    email,
    contactPersonName,
    mobileNumber,
    billingAddress,
    billingCity,
    billingState,
    billingPincode,
    gstin,
    shippingAddress,
    shippingCity,
    shippingState,
    shippingPincode,
    orderDate,
    orderCreatedBy,
    orderNumber,
    orderCreatedByNumber,
  } = customerInfo;

  const hasItemWiseDiscount = customerOrders.some(
    (item: any) => item.discountPrice && item.discountPrice > 0,
  );

  const subTotal = customerOrders.reduce((sum, item, index) => {
    const qty = orderItems[index]?.quantity || 1;
    return sum + item.price * qty;
  }, 0);

  const gstMap = new Map<number, { taxable: number; tax: number }>();
  let totalItemDiscounts = 0;

  customerOrders.forEach((item: any, index: number) => {
    const qty = orderItems[index]?.quantity || 1;
    const originalAmount = item.price * qty;
    const itemDisc = item.discountPrice || 0;
    totalItemDiscounts += itemDisc;

    const taxableAmount = originalAmount - itemDisc;
    const rate = (item as any).gst || (item as any).productId?.gst || 0;
    const taxAmt = (taxableAmount * rate) / 100;

    if (!gstMap.has(rate)) {
      gstMap.set(rate, { taxable: 0, tax: 0 });
    }

    const entry = gstMap.get(rate)!;
    entry.taxable += taxableAmount;
    entry.tax += taxAmt;
  });

  const totalTax = Array.from(gstMap.values()).reduce(
    (acc, e) => acc + e.tax,
    0,
  );
  const finalTaxableAmount = subTotal - totalItemDiscounts;
  const freight = Number(transportPrice) || 0;
  const finalGrandTotal = finalTaxableAmount + totalTax + freight;

  const formatValue = (n: number): string => {
    const num = Number(n);
    if (isNaN(num)) return "0.00";
    return num.toFixed(2);
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.mainContent}>
          <View style={styles.headerRow}>
            <View style={styles.leftBlock}>
              <Image src={companyLogo} style={styles.logo} />
              <View style={styles.companyAndQuoteRow}>
                <View style={styles.companyBlock}>
                  <Text style={styles.companyName}>
                    Frelit Energy Pvt. Ltd.
                  </Text>
                  <Text style={styles.companyAddress}>
                    B-20, Bhagya Laxmi Society,{"\n"}Beside Aton Gym, Near
                    Darbar Chokdi, {"\n"}Manjalpur, Vadodara, Gujarat 390011
                  </Text>
                  <Text style={styles.gstInfo}>GSTIN/UIN: 24AADCF7701R1ZB</Text>
                </View>
                <View style={styles.quotationBox}>
                  <Text style={styles.quotationText}>Proforma</Text>
                  <Text style={styles.quotationText}>invoice</Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.companyInfo}>
            <View style={styles.addressContainer}>
              <Text style={styles.boldText}>Shipping Address:</Text>
              <View style={styles.addressRow}>
                <Text style={styles.addressLabel}>Address:</Text>
                <Text style={styles.addressValue}>{shippingAddress}</Text>
              </View>
              <View style={styles.addressRow}>
                <Text style={styles.addressLabel}>City:</Text>
                <Text style={styles.addressValue}>{shippingCity}</Text>
              </View>
              <View style={styles.addressRow}>
                <Text style={styles.addressLabel}>State:</Text>
                <Text style={styles.addressValue}>{shippingState}</Text>
              </View>
              <View style={styles.addressRow}>
                <Text style={styles.addressLabel}>Pincode:</Text>
                <Text style={styles.addressValue}>{shippingPincode}</Text>
              </View>
              <View style={styles.piInfoContainer}>
                <View style={styles.piInfoRow}>
                  <Text style={styles.addressLabel}>PI No:</Text>
                  <Text style={styles.addressValue}>{orderNumber}</Text>
                </View>
                <View style={styles.piInfoRow}>
                  <Text style={styles.addressLabel}>PI Date:</Text>
                  <Text style={styles.addressValue}>{orderDate}</Text>
                </View>
                <View style={styles.piInfoRow}>
                  <Text style={styles.addressLabel}>Created By:</Text>
                  <Text style={styles.addressValue}>{orderCreatedBy}</Text>
                </View>
                <View style={styles.piInfoRow}>
                  <Text style={styles.addressLabel}>Contact No:</Text>
                  <Text style={styles.addressValue}>
                    {orderCreatedByNumber}
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.addressContainer}>
              <Text style={styles.sectionTitle}>Invoice To:</Text>
              <Text style={styles.billingTitle}>Billing Details:</Text>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Company:</Text>
                <Text style={styles.value}>{name}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Kind attn:</Text>
                <Text style={styles.value}>{contactPersonName}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Phone:</Text>
                <Text style={styles.value}>{mobileNumber}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Email:</Text>
                <Text style={styles.value}>{email}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Address:</Text>
                <Text style={styles.value}>{billingAddress}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>City:</Text>
                <Text style={styles.value}>{billingCity}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>State:</Text>
                <Text style={styles.value}>{billingState}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Pincode:</Text>
                <Text style={styles.value}>{billingPincode}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>GSTIN:</Text>
                <Text style={styles.value}>{gstin}</Text>
              </View>
            </View>
          </View>

          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableHeaderText, styles.slNoCell]}>
                SR NO.
              </Text>
              <Text style={[styles.tableHeaderText, styles.descriptionCell]}>
                ITEM DESCRIPTION
              </Text>
              <Text style={[styles.tableHeaderText, styles.hsnCell]}>HSN</Text>
              <Text style={[styles.tableHeaderText, styles.qtyCell]}>
                QTY (UNIT)
              </Text>
              <Text style={[styles.tableHeaderText, styles.rateCell]}>
                RATE (INR)
              </Text>
              {hasItemWiseDiscount && (
                <Text
                  style={[
                    styles.tableHeaderText,
                    { width: "10%", textAlign: "center" },
                  ]}
                >
                  DISC (INR)
                </Text>
              )}
              <Text style={[styles.tableHeaderText, styles.totalCell]}>
                TOTAL (INR)
              </Text>
            </View>

            {customerOrders.map((item: any, index) => {
              const qty = orderItems[index]?.quantity || 1;
              const originalAmount = qty * item.price;
              const itemDisc = item.discountPrice || 0;
              const lineTotal = originalAmount - itemDisc;
              return (
                <View key={index} style={styles.tableRow}>
                  <Text style={[styles.tableCell, styles.slNoCell]}>
                    {index + 1}
                  </Text>
                  <Text style={[styles.tableCellDesc, styles.descriptionCell]}>
                    {item.productName}
                  </Text>
                  <Text style={[styles.tableCell, styles.hsnCell]}>
                    {item.hsn}
                  </Text>
                  <Text style={[styles.tableCell, styles.qtyCell]}>{qty}</Text>
                  <Text style={[styles.tableCell, styles.rateCell]}>
                    {item.price.toLocaleString()}
                  </Text>
                  {hasItemWiseDiscount && (
                    <Text
                      style={[
                        styles.tableCell,
                        { width: "10%", textAlign: "center" },
                      ]}
                    >
                      {itemDisc > 0 ? itemDisc.toLocaleString() : "—"}
                    </Text>
                  )}
                  <Text style={[styles.tableCell, styles.totalCell]}>
                    {lineTotal.toLocaleString()}
                  </Text>
                </View>
              );
            })}
          </View>

          <View style={styles.totals}>
            <View style={[styles.totalRow, { padding: 4 }]}>
              <Text style={styles.totalLabel}>Sub‑total</Text>
              <Text style={styles.totalValue}>INR {formatValue(subTotal)}</Text>
            </View>
            {totalItemDiscounts > 0 && (
              <View style={[styles.totalRow, { padding: 4 }]}>
                <Text style={styles.totalLabel}>Discount</Text>
                <Text style={styles.totalValue}>
                  - INR {formatValue(totalItemDiscounts)}
                </Text>
              </View>
            )}
            {Array.from(gstMap.entries()).map(([rate, { tax }]) => (
              <View key={rate} style={[styles.totalRow, { padding: 4 }]}>
                <Text style={styles.totalLabel}>Tax ({rate}%)</Text>
                <Text style={styles.totalValue}>INR {formatValue(tax)}</Text>
              </View>
            ))}
            {transportPrice > 0 && (
              <View style={[styles.totalRow, { padding: 4 }]}>
                <Text style={styles.totalLabel}>Freight</Text>
                <Text style={styles.totalValue}>
                  INR {formatValue(transportPrice)}
                </Text>
              </View>
            )}
            <View
              style={[
                styles.totalRow,
                { backgroundColor: "#0057B7", padding: 6 },
              ]}
            >
              <Text
                style={[
                  styles.totalLabel,
                  { color: "#fff", fontWeight: "bold" },
                ]}
              >
                Total
              </Text>
              <Text
                style={[
                  styles.totalValue,
                  { color: "#fff", fontWeight: "bold" },
                ]}
              >
                INR {formatValue(finalGrandTotal)}
              </Text>
            </View>
          </View>

          <View
            style={{
              flexDirection: "row",
              marginTop: 6,
              justifyContent: "space-between",
            }}
          >
            <View style={{ flex: 1 }}>
              <Text style={styles.termsTitle}>TERMS AND CONDITIONS</Text>
              {[
                "Payment Terms: 100% Advance",
                "Validity: 15 days from date of issue",
                "Delivery: Within 5–7 working days after order confirmation",
                "Freight charges approximate and vary at dispatch",
              ].map((txt, idx) => (
                <Text key={idx} style={styles.termsItem}>
                  • {txt}
                </Text>
              ))}
            </View>
            <View style={{ width: 250 }}>
              <Text style={styles.bankTitle}>PAYMENT BANK DETAILS</Text>
              <View style={styles.bankDetailsTable}>
                <View style={styles.bankRow}>
                  <Text style={styles.bankLabel}>Account No.:</Text>
                  <Text style={styles.bankValue}>922030048197928</Text>
                </View>
                <View style={styles.bankRow}>
                  <Text style={styles.bankLabel}>Account Name:</Text>
                  <Text style={styles.bankValue}>Frelit Energy Pvt. Ltd.</Text>
                </View>
                <View style={styles.bankRow}>
                  <Text style={styles.bankLabel}>IFSC:</Text>
                  <Text style={styles.bankValue}>UTIB0002999</Text>
                </View>
                <View style={styles.bankRow}>
                  <Text style={styles.bankLabel}>Bank Name:</Text>
                  <Text style={styles.bankValue}>Axis Bank Ltd.</Text>
                </View>
                <View style={styles.bankRow}>
                  <Text style={styles.bankLabel}>Branch Name:</Text>
                  <Text style={styles.bankValue}>Maneja</Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.footerContainer} fixed>
          <View style={styles.footer}>
            <Text style={styles.footerText}>www.frelitenergy.com</Text>
            <Text style={styles.footerText}>079-41056633</Text>
            <Text style={styles.footerText}>sales@frelitenergy.com</Text>
          </View>
          <View style={styles.subFooter}>
            <Text style={styles.jurisdictionText}>
              Subject to Vadodara Jurisdiction
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};
